import * as _ from 'lodash';
import { ICenterBase, IContactEntityTypePositions, IListItem } from '@nkr/models/index';
import { Button, DropdownButton, FormControl, MenuItem } from 'react-bootstrap';
import { IFilters } from '../filters';
import Select, { components, OptionProps } from 'react-select';
import IRoleItem from '../role-item';

const CheckboxOption = (props: OptionProps<IRoleItem, true>) => {
    return (
        <div>
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                />{" "}
                <span>{props.label}</span>
            </components.Option>
        </div>
    );
};

interface IProps {
    filters: IFilters,
    centers: ICenterBase[],
    contactEntityTypes: IListItem[],
    roles: IContactEntityTypePositions[],
    showDeleted: boolean,
    canHardDelete: boolean,
    currentUser?: any,
    contacts?: any[],
    onSearch: () => void,
    onChange: (filters: IFilters) => void,
    onShowDeletedChange: (e: React.FormEvent<FormControl & HTMLSelectElement>) => void,
    onAdd: () => void,
    onExport: () => void,
    onOutlookExport: () => void,
    onReset: () => void,
    onBulkDelete: () => void,
}

const AdminFilters = (props: IProps) => {
    const getSelectStyles = () => {
        let customStyles = {
            container: (provided) => ({
                ...provided,
                minHeight: 30,
                minWidth: 400
            }),
            control: (base) => ({
                ...base,
                minHeight: 30,
                padding: 0,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                boxShadow: '0 none'
            }),
            indicatorsContainer: (provided) => ({
                ...provided,
                minHeight: 30
            }),
            clearIndicator: (provided) => ({
                ...provided,
                minHeight: 30,
                padding: 3
            }),
            dropdownIndicator: (provided) => ({
                ...provided,
                minHeight: 30,
                padding: 3
            }),
            indicatorSeparator: () => ({
                color: 'white'
            }),
            valueContainer: (provided) => ({
                ...provided,
                top: '-1px',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                // display: 'flex'
                padding: '0 8px',
            }),
            multiValue: (provided) => ({
                ...provided,
                // marginTop: 0,
                //margin: '3px'
                whiteSpace: 'normal',
            }),
            input: (provided, state) => ({
                ...provided,
                // Ensures the input itself can wrap and doesn't force a single line layout
                whiteSpace: 'pre-wrap',
            }),
            placeholder: (provided) => ({
                ...provided,
            }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: 'white',
                color: '#333',
                paddingTop: '2px',
                paddingBottom: '2px'
            }),
            groupHeading: (provided) => ({
                ...provided,
                fontWeight: 'bold',
                color: '#333',
                fontSize: 12
            }),
            menuList: (provided) => ({
                ...provided,
                maxHeight: 600
            })
        };

        return customStyles;
    };

    const getRoles = () => {
        return props.roles.map(item => {
            let optionList = item.positions.map(p => {
                return {
                    label: p.description,
                    value: `${item.entityTypeId}|${p.id}`,
                    extra: {
                        entityType: item.entityTypeId,
                        positionId: p.id
                    }
                };
            });

            return {
                label: item.entityType,
                options: _.sortBy(optionList, x => x.label)
            };
        });
    };

    const handleCenterChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement>) => {
        let filters = _.cloneDeep(props.filters);
        filters.centerId = parseInt(e.currentTarget.value);

        props.onChange(filters);
    };

    const handleContactEntityTypeChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement>) => {
        let filters = _.cloneDeep(props.filters);
        filters.contactEntityTypeId = parseInt(e.currentTarget.value);

        props.onChange(filters);
    };

    const handleRoleChange = (selected: IRoleItem[]) => {
        let filters = _.cloneDeep(props.filters);
        filters.adminRoles = selected ? selected : [];

        props.onChange(filters);
    };

    const handleSearchChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let filters = _.cloneDeep(props.filters);
        filters.search = e.currentTarget.value;

        props.onChange(filters);
    };

    const roleOptions = getRoles();

    return (
        <div className='container-fluid' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                <div>Center:&nbsp;</div>
                <FormControl
                    componentClass='select'
                    style={{ width: 'initial' }}
                    value={props.filters.centerId}
                    onChange={handleCenterChange}
                >
                    <option value={0}>- ALL CENTERS -</option>
                    {props.centers.map((center, index) =>
                        <option key={'ctr-' + index} value={center.id}>{center.alias}</option>
                    )}
                </FormControl>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                <div>Types:&nbsp;</div>
                <FormControl
                    componentClass='select'
                    style={{ width: 'initial' }}
                    value={props.filters.contactEntityTypeId}
                    onChange={handleContactEntityTypeChange}
                >
                    <option value={0}>- ALL TYPES -</option>
                    {props.contactEntityTypes.map((item, index) =>
                        <option key={'et-' + index} value={item.id}>{item.description}</option>
                    )}
                </FormControl>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                <div>Search:&nbsp;</div>
                <FormControl
                    style={{ width: 125 }}
                    placeholder='Search by name...'
                    value={props.filters.search}
                    onChange={handleSearchChange}
                />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', maxWidth: '40vw' }}>
                <div>Role:&nbsp;</div>
                <div style={{ marginRight: 10 }}>
                    <Select
                        styles={getSelectStyles()}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        placeholder='Select roles...'
                        options={roleOptions}
                        components={{ Option: CheckboxOption }}
                        value={props.filters.adminRoles}
                        onChange={handleRoleChange}
                    />
                </div>
            </div>

            {props.canHardDelete && (
                <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
                    <div>Show Deleted:&nbsp;</div>
                    <FormControl
                        componentClass='select'
                        style={{ width: 'initial' }}
                        value={props.showDeleted ? '1' : '0'}
                        onChange={props.onShowDeletedChange}
                    >
                        <option value='0'>No</option>
                        <option value='1'>Yes</option>
                    </FormControl>
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
                <Button bsStyle='primary' onClick={props.onSearch}>Search</Button>&nbsp;
                <Button bsStyle='default' onClick={props.onReset}>Reset</Button>
            </div>

            <div>
                <DropdownButton pullRight bsStyle='primary' title='Actions' id='Actions'>
                    <MenuItem onClick={props.onAdd}>Add Contact</MenuItem>
                    <MenuItem onClick={props.onExport}>Export Contacts</MenuItem>
                    <MenuItem onClick={props.onOutlookExport}>Outlook Export</MenuItem>
                    {props.canHardDelete && (
                        <>
                            <MenuItem divider />
                            <MenuItem onClick={props.onBulkDelete} style={{ color: 'red', fontWeight: 'bold' }}>
                                ⚠️ Bulk Hard Delete
                                    </MenuItem>
                        </>
                    )}
                </DropdownButton>
            </div>
        </div>
    );
}

export default AdminFilters;